// Pull a list down from its top to refresh it (touch screens; the list's own refresh button is
// the tap twin). The page keeps scrolling normally; only a downward pull that starts at the very
// top is taken over.
import { useEffect, useRef, useState } from "react";
import { View } from "tamagui";
import { useT } from "../lib/i18n";
import { haptic } from "./gestures/haptics";
import { Spinner } from "./Spinner";
import { Caption } from "./text";

const TRIGGER_PX = 72;

export interface PullToRefreshProps {
  onRefresh: () => Promise<unknown>;
  children: React.ReactNode;
}

export function PullToRefresh({ onRefresh, children }: PullToRefreshProps): React.JSX.Element {
  const { t } = useT();
  const root = useRef<React.ComponentRef<typeof View>>(null);
  const [pull, setPull] = useState(0);
  const [busy, setBusy] = useState(false);
  const latest = useRef(onRefresh);
  useEffect(() => {
    latest.current = onRefresh;
  });

  useEffect(() => {
    const el = root.current;
    if (!(el instanceof HTMLElement)) return undefined;
    let startY: number | undefined;
    let distance = 0;
    const onStart = (event: TouchEvent): void => {
      startY = window.scrollY <= 0 ? event.touches[0]?.clientY : undefined;
      distance = 0;
    };
    const onMove = (event: TouchEvent): void => {
      const y = event.touches[0]?.clientY;
      if (startY === undefined || y === undefined) return;
      distance = Math.max(0, y - startY);
      if (distance === 0) return;
      if (event.cancelable) event.preventDefault();
      setPull(Math.min(distance / 2, TRIGGER_PX * 1.5));
    };
    const onEnd = (): void => {
      const fire = startY !== undefined && distance / 2 >= TRIGGER_PX;
      startY = undefined;
      setPull(0);
      if (!fire) return;
      haptic("commit");
      setBusy(true);
      void latest.current().finally(() => setBusy(false));
    };
    el.addEventListener("touchstart", onStart, { passive: true });
    el.addEventListener("touchmove", onMove, { passive: false });
    el.addEventListener("touchend", onEnd);
    el.addEventListener("touchcancel", onEnd);
    return () => {
      el.removeEventListener("touchstart", onStart);
      el.removeEventListener("touchmove", onMove);
      el.removeEventListener("touchend", onEnd);
      el.removeEventListener("touchcancel", onEnd);
    };
  }, []);

  const armed = pull >= TRIGGER_PX;
  return (
    <View ref={root}>
      <View
        height={busy ? 40 : pull}
        overflow="hidden"
        alignItems="center"
        justifyContent="center"
        aria-live="polite"
      >
        {busy ? (
          <Spinner label={t("common.pull.busy")} />
        ) : (
          pull > 8 && <Caption>{armed ? t("common.pull.release") : t("common.pull.hint")}</Caption>
        )}
      </View>
      {children}
    </View>
  );
}
